import type { IScene } from './IScene';

/**
 * Scene — substrate-agnostic abstract base class for game scenes.
 *
 * Phase H10 Arc λ.9 Stage D-2 (2026-05-10). No longer extends Phaser.Scene
 * — pure IScene impl. SceneRegistry instantiates scenes directly and
 * drives lifecycle (init / preload / create / update / shutdown) without
 * Phaser's scene-plugin tree.
 *
 * Subclasses construct via `super('SceneKey')`. Bodies must NOT reach
 * into `this.events / this.add / this.tweens / this.cameras / this.input
 * / this.time / this.load` — those Phaser scene plugins are gone. Use
 * the substrate-agnostic shims instead:
 *   - DOMInput / DOMTimer / TweenHost
 *   - createGraphics / createSprite / createNameTag / etc. factories
 *   - CameraSystem.getICamera() for camera reads
 *   - EventBus for cross-system events
 *
 * `onShutdown(fn)` wires explicitly through SceneRegistry — no more
 * `this.events.once('shutdown')` reach-through. SceneRegistry.stop()
 * calls `scene.shutdown?.()` which invokes registered listeners.
 */
export abstract class Scene implements IScene {
  /** Stable scene key — required by the IScene contract. Set in constructor. */
  public readonly key: string;

  private readonly _shutdownListeners = new Set<() => void>();

  constructor(key: string) {
    this.key = key;
  }

  /**
   * Subscribe to the scene's shutdown event. Returns an unsubscriber.
   *
   * Listeners fire when SceneRegistry.stop() invokes scene.shutdown().
   * Errors are caught — one bad listener doesn't stop the others.
   */
  public onShutdown(fn: () => void): () => void {
    this._shutdownListeners.add(fn);
    return () => { this._shutdownListeners.delete(fn); };
  }

  /**
   * IScene.shutdown — invoked by SceneRegistry.stop(). Subclasses may
   * override to add custom teardown; if they do, MUST call super.shutdown()
   * to flush registered onShutdown listeners.
   */
  public shutdown(): void {
    for (const listener of this._shutdownListeners) {
      try { listener(); } catch { /* swallow — same shape as DOMInput */ }
    }
    this._shutdownListeners.clear();
  }
}
